import * as ents from '@attain-apps/entities'
import { Word } from '@attain-apps/string-builder'

import { DEFAULT_DURATION_MS } from './config'

/**
 * Payload passed to action callbacks when a toast action is clicked.
 */
export interface ActionCallbackParams {
  toastId: Toast.Id
  actionId: Toast.Action['id']
  value?: string
}

/**
 * Runtime-only callback for interactive toast actions.
 */
export type ActionCallback = (
  params: ActionCallbackParams,
) => void | Promise<void>

/**
 * Input accepted by toaster `show` action payloads.
 *
 * Action callbacks are declared directly on each action to keep the API
 * straightforward:
 * `{ actions: [{ id: 'retry', callback: () => ... }] }`
 */
export interface Input extends Omit<ents.core.display.Toast, 'actions'> {
  actions?: readonly Input.Action[]
}
export namespace Input {
  /**
   * Frontend toast action model. This extends the shared transport shape with a
   * runtime-only callback.
   */
  export interface Action extends ents.core.display.Toast.Action {
    callback?: ActionCallback
  }
}

/**
 * Input accepted by `actions.show`.
 */
export type ShowInput = ExactlyOneOf<{
  toast: Input
  toasts: readonly Input[]
}>
export namespace ShowInput {
  /**
   * Converts show input into normalized toasts with defaults applied.
   */
  export function from(input: ShowInput): readonly Toast[] {
    if (input.toasts) {
      return input.toasts.map(Toast.from)
    }
    return input.toast ? [Toast.from(input.toast)] : []
  }
}

export interface Toast extends Omit<ents.core.display.Toast, 'actions'> {
  id: Toast.Id
  style: ents.core.display.Style
  durationMs: number | null
  dismissible: boolean
  actions: readonly Toast.Action[]
}
export namespace Toast {
  export type Id = Word

  export namespace DurationMs {
    /**
     * Resolves toast duration:
     * - `null` => sticky (manual dismiss only)
     * - `undefined` => default duration
     * - `number` => clamped to `>= 0`
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from(durationMs: number | null | undefined): number | null {
      if (durationMs === null) {
        return null
      }
      return Math.max(0, durationMs ?? DEFAULT_DURATION_MS)
    }
  }

  export interface Action {
    /**
     * Stable action identifier, unique within the toast.
     */
    id: string
    /**
     * Label shown to the user.
     *
     * If omitted, UI may fall back to `id`.
     */
    name?: string
    /**
     * Visual treatment of the action.
     *
     * @default 'link'
     */
    kind: 'button' | 'link'
    /**
     * Optional value forwarded to callback payload.
     */
    value?: string
    /**
     * Whether clicking this action should dismiss the toast.
     *
     * @default true
     */
    closeOnClick: boolean
    /**
     * Runtime-only click handler executed by toaster effects.
     */
    callback?: ActionCallback
  }
  export namespace Action {
    /**
     * Creates normalized action model with defaults applied.
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from(action: Input.Action): Toast.Action {
      return {
        callback: action.callback,
        closeOnClick: action.closeOnClick ?? true,
        id: action.id,
        kind: action.kind ?? 'link',
        name: action.name,
        value: action.value,
      }
    }
  }

  /**
   * Creates normalized toast model with defaults applied.
   */
  export function from(input: Input): Toast {
    return {
      actions: (input.actions ?? []).map(Action.from),
      description: input.description,
      dismissible: input.dismissible ?? true,
      durationMs: DurationMs.from(input.durationMs),
      id: Word.generate(),
      name: input.name,
      style: input.style ?? ents.core.display.Style.Info,
    }
  }
}
